import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/nextjs'
import { IAdvertCard } from 'data-transfers/dto'
import { IAdvertResponse } from 'data-transfers/responses'
import { format } from 'date-fns'
import { AdvertVisibilityType, EmploymentStatus } from 'enums'

import { Advert as AdvertComponent } from 'components/Advert'
import AdvertSwiper from 'components/AdvertSwiper/AdvertSwiper'
import { IMetaTagsProps } from 'components/MetaTags/types'
import { IMarkupSchemas, JobPostingData } from 'components/SEOMarkupSchema'

import { useAdvert } from 'hooks/useAdvert'
import { getAdvert, getAdvertsPublic } from 'hooks/useAdvert/advert.service'
import { useLanguageSettings } from 'context/language-manager'

import { publicClient } from 'utils'
import adUtils from 'utils/adUtil'
import { getEnv } from 'utils/env/env.util'
import { getDefaultMetaPropsWithUrl, getFirstSentence } from 'utils/meta.util'
import { getFullPageByUID } from 'utils/prismic'

const { baseUrl } = getEnv()
import { getLocalisedLink } from 'utils'
import checkUtil from 'utils/check.util'

const Index = (props: { ad: IAdvertResponse; adverts?: IAdvertCard[] }) => {
  const { switchLanguage } = useLanguageSettings()
  const { locale, asPath, push, query } = useRouter()
  const { data: ad } = useAdvert({
    payload: {
      id: props.ad.id ?? '',
      includeAllTranslations: true,
      languageCode: locale,
      trackingReview: true
    },
    options: {
      initialData: props.ad,
      refetchOnWindowFocus: false
    }
  })

  useEffect(() => {
    if (
      ad &&
      ad.slug !== query.id &&
      ad.slugs.length === 2 &&
      ad.slugs?.find((s) => s.slug === query.id)
    ) {
      push(`${getLocalisedLink('advert', locale)}${ad.slug}`)
    }
  }, [ad, asPath, locale, query.id, push, switchLanguage])

  const advert = useMemo(() => ad ?? props.ad, [ad, props.ad])

  return (
    <>
      <div className="pt-10">
        <AdvertComponent ad={advert} apply showVisibilityStatus />
      </div>
      {props.adverts && (
        <div className=" print:hidden">
          <AdvertSwiper adverts={props.adverts} />
        </div>
      )}
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale: currentLocale,
  previewData,
  defaultLocale
}) => {
  const locale = currentLocale || defaultLocale || 'en'
  const advertsPagePath = getLocalisedLink('adverts', locale)
  const advertPagePath = getLocalisedLink('advert', locale)
  const id = params?.id as string
  try {
    const id = params?.id as string
    const ad = await getAdvert({
      id,
      languageCode: locale,
      includeAllTranslations: true
    })

    if (!ad) {
      await publicClient.post(`/mailing/send`, {
        sendTo: 'marian@devima.solutions',
        subject: 'Not Found',
        plainTextContent: `AdvertId: ${id}\nDatetime: ${new Date().toISOString()}\nEnv: ${
          process.env.API_URL
        }; ${process.env.NEXT_PUBLIC_APP_ENV}`,
        htmlContent: `AdvertId: ${id}\nDatetime: ${new Date().toISOString()}\nEnv: ${
          process.env.API_URL
        }; ${process.env.NEXT_PUBLIC_APP_ENV}`
      })
      return {
        notFound: true
      }
    }

    if (checkUtil.isUUID(id)) {
      return {
        redirect: {
          permanent: true,
          destination: `${advertPagePath}${ad.slug}`
        }
      }
    }

    const advertsPromise = getAdvertsPublic({
      languageCode: locale,
      limit: 20,
      statuses: [AdvertVisibilityType.Active],
      orderingColumn: 'dateCreated',
      order: 'desc'
    })
    //   Prismic
    const pagePartsPromise = getFullPageByUID(
      'page',
      'notfound',
      { locale },
      { previewData }
    )
    const i18nPromise = serverSideTranslations(
      locale,
      [
        'home_adverts',
        'actions',
        'ad',
        'org',
        'ad_form',
        'zod',
        'inputs',
        'crm',
        'enums'
      ],
      null,
      adUtils.getAdvertExtraTranslation(ad.translation)
    )

    const [pageParts, i18n, adverts] = await Promise.all([
      pagePartsPromise,
      i18nPromise,
      advertsPromise
    ])
    const { page, ...layoutParts } = pageParts

    const title = adUtils.getAdvertTitle({
      ad: ad,
      brandName: ad.organisation?.brandName,
      locale
    })
    const metaDescription = getFirstSentence(ad?.description)
    const seo: IMetaTagsProps = {
      title: `${title} - HRizer`,
      metaDescription,
      metaProps: getDefaultMetaPropsWithUrl(
        `${ad.position ?? ''}, ${ad.positionLevel ?? ''}`
      )
    }

    const jobSchema: JobPostingData = {
      title: adUtils.getAdvertTitle({
        ad: ad,
        locale
      }),
      description: ad.description ?? '',
      datePosted: format(new Date(ad.publishDate ?? Date.now()), 'yyyy-MM-dd'),
      identifier: {
        name: ad.organisation.brandName,
        value: ad.id
      },
      employmentType: [
        ad.employmentStatus == EmploymentStatus.FullTime
          ? 'FULL_TIME'
          : 'PART_TIME'
      ],
      applicantLocationRequirements: ad.address?.country
        ? ad.address?.country ?? ad.organisation.countryCode
        : ad.organisation.countryCode,
      hiringOrganization: {
        name: ad.organisation.brandName,
        logo: ad.organisation.logoUrl,
        sameAs: ad.organisation.websiteUrl
      },
      baseSalary: { minValue: ad.salaryFrom, maxValue: ad.salaryTo },
      jobLocation: ad.address
        ? {
            addressCountry: ad.address.country,
            addressRegion: ad.address.city,
            postalCode: ad.address.zipCode ?? '',
            streetAddress: ad.address.streetAddress
          }
        : null,
      //not 'By Agreement' and '0 days / week'
      jobLocationType:
        ad.remoteWorkTypeId !== '267cd912-53df-4f43-a17b-08da9d61563e' &&
        ad.remoteWorkTypeId !== '98762f88-d94a-4a2b-c04a-08d98104d557'
          ? 'TELECOMMUTE'
          : null
    }

    const markupSchema: IMarkupSchemas = [
      {
        type: 'BreadcrumbList',
        data: {
          items: [
            { name: 'Home', item: baseUrl },
            { name: 'All Adverts', item: `${baseUrl}${advertsPagePath}` },
            { name: title }
          ]
        }
      }
    ]
    if (ad.visibilityType == AdvertVisibilityType.Active) {
      markupSchema.push({
        type: 'JobPosting',
        data: jobSchema
      })
    }
    return {
      props: {
        ...i18n,
        ad,
        page,
        seo,
        adverts: adverts.data,
        internal: {
          layout: {
            className: 'bg-gray-50',
            ...layoutParts
          }
        },
        markupSchema
      },
      revalidate: 60 * 60 * 4
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { advertId: id }
    })
    await publicClient.post(`/mailing/send`, {
      sendTo: 'marian@devima.solutions',
      subject: 'Exception',
      plainTextContent: `AdvertId: ${id}\nDatetime: ${new Date().toISOString()}\nEnv: ${
        process.env.API_URL
      }; ${process.env.NEXT_PUBLIC_APP_ENV}; ${JSON.stringify(error)}`,
      htmlContent: `AdvertId: ${id}\nDatetime: ${new Date().toISOString()}\nEnv: ${
        process.env.API_URL
      }; ${process.env.NEXT_PUBLIC_APP_ENV}; ${JSON.stringify(error)}`
    })
    throw error
  }
}

export default Index
